import { apiGet, apiPost, apiPut, apiDelete } from './api';
import API_CONFIG from '../config/api';

/* =========================
   CLUBS (ADMIN)
========================= */

export const getClubs = async (page = 1, limit = 10, search = '') => {
  let url = `${API_CONFIG.ENDPOINTS.CLUBS.LISTAR}?page=${page}&limit=${limit}`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  const response = await apiGet(url);
  const data = await response.json();

  // Compatibilidad: si retorna array directo (versión anterior)
  if (Array.isArray(data)) {
    return {
      items: data,
      pagination: {
        page: 1,
        limit: data.length,
        total: data.length,
        total_pages: 1,
        has_next: false,
        has_prev: false
      }
    };
  }

  if (data.success === false) {
    throw new Error(data.error || 'Error al listar clubs');
  }

  return {
    items: data.items || [],
    pagination: data.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      total_pages: 1,
      has_next: false,
      has_prev: false
    }
  };
};

export const crearClub = async (payload) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.CLUBS.CREAR,
    payload
  );

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error || 'Error al crear club');
  }

  return data;
};

export const editarClub = async (id, payload) => {
  const response = await apiPut(
    API_CONFIG.ENDPOINTS.CLUBS.EDITAR(id),
    payload
  );

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error || 'Error al editar club');
  }

  return data;
};

export const eliminarClub = async (id) => {
  const response = await apiDelete(
    API_CONFIG.ENDPOINTS.CLUBS.ELIMINAR(id)
  );

  const data = await response.json();

  if (!data.ok) {
    throw new Error('No se pudo eliminar el club');
  }

  return data;
};

/* =========================
   ASOCIADOS (página pública: clubs + gyms)
========================= */

/**
 * Convierte descripción en lista de "servicios" para la tarjeta.
 * Si hay texto, lo parte por saltos o comas; si no, devuelve un default por tipo.
 */
function descripcionAServicios(descripcion, tipo) {
  if (descripcion && typeof descripcion === 'string') {
    const parts = descripcion.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    if (parts.length > 0) return parts;
  }
  return tipo === 'club' ? ['Entrenamiento', 'Comunidad'] : ['Entrenamiento', 'Equipamiento'];
}

/**
 * Lista clubs y gyms activos para la página Asociados (formato unificado).
 */
export const getAsociadosPublic = async () => {
  const [clubsRes, gymsRes] = await Promise.all([
    apiGet(`${API_CONFIG.ENDPOINTS.CLUBS.LISTAR}?limit=100&estado=activo`),
    apiGet(`${API_CONFIG.ENDPOINTS.GYMS.LISTAR}?limit=100&estado=activo`)
  ]);

  const clubsData = await clubsRes.json();
  const gymsData = await gymsRes.json();

  if (!clubsRes.ok || clubsData.success === false) {
    throw new Error(clubsData.error || clubsData.message || 'Error al cargar clubes');
  }
  if (!gymsRes.ok || gymsData.success === false) {
    throw new Error(gymsData.error || gymsData.message || 'Error al cargar gimnasios');
  }

  const clubs = (clubsData.items || []).map((c) => ({
    id: `club-${c.id}`,
    nombre: c.nombre,
    tipo: 'club',
    region: c.region || '',
    ciudad: c.ciudad || '',
    direccion: c.direccion || '',
    telefono: c.telefono || '',
    email: c.email || '',
    sitio_web: c.sitio_web || '',
    logo_url: c.logo_url || null,
    miembros: c.miembros ?? null,
    servicios: descripcionAServicios(c.descripcion, 'club')
  }));

  const gyms = (gymsData.items || []).map((g) => ({
    id: `gym-${g.id}`,
    nombre: g.nombre,
    tipo: 'gimnasio',
    region: g.region || '',
    ciudad: g.ciudad || '',
    direccion: g.direccion || '',
    telefono: g.telefono || '',
    email: g.email || '',
    sitio_web: g.sitio_web || '',
    logo_url: g.logo_url || null,
    servicios: descripcionAServicios(g.descripcion, 'gimnasio')
  }));

  return [...clubs, ...gyms];
};
